import React, { memo } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import AutoSizer from 'react-virtualized-auto-sizer'
import { VariableSizeList, areEqual } from 'react-window'
import { Spinner } from '@blueprintjs/core'
import DataPlaceholder from 'src/components/DataPlaceholder'
import { getRunbook, setDepthLevel } from 'src/store/modules/runbooks'
import {
    getSelectedActions,
    getVisibleActionUids,
    getVisibleOrphans,
    moveAction,
    updateAction,
} from 'src/store/modules/runbooks/actions'
import { clearFocusedAction } from 'src/store/modules/ui/runbook'
import noActionsImage from 'src/assets/icons/no-actions.svg'
import Action from './Action'
import { getColumnStyle } from './columns'
import styles from './actionsList.module.scss'

const COMBINE_CLASS_NAME = styles.combine
const ROW_SIZE = 42

// Modify the HEADERS array to dynamically adjust based on execution mode
const getHeaders = ({ isEditing, activeSession }) => {
    const inExecutionMode = Boolean(activeSession)
    const inEditMode = Boolean(isEditing) && !inExecutionMode

    const showSelect = inEditMode || inExecutionMode
    const showStatus = inExecutionMode
    const showTime = !inEditMode

    // The action name takes the remaining space and truncates with an ellipsis,
    // the other columns keep their size (or shrink down to their minWidth)
    // so nothing overlaps when the viewport gets narrower (e.g. tablets).
    return [
        { key: 'id', content: 'ID', width: '6.25rem' },
        showSelect
            ? { key: 'select', content: null, width: '3.75rem', centered: true }
            : null,
        showStatus ? { key: 'status', content: 'Status', width: '4rem' } : null,
        {
            key: 'actionName',
            content: 'Action name',
            width: '0',
            grow: true,
            minWidth: '7.5rem',
        },
        showTime
            ? {
                  key: 'time',
                  content: null,
                  width: '12%',
                  shrink: true,
                  minWidth: '2.5rem',
              }
            : null,
        {
            key: 'dependencies',
            content: 'Dependencies',
            width: '16%',
            shrink: true,
            minWidth: '3.75rem',
        },
        {
            key: 'owner',
            content: 'Owner',
            width: '21%',
            shrink: true,
            minWidth: '6.875rem',
        },
        { key: 'menu', width: '2.5rem', centered: true },
    ].filter(Boolean)
}

const Row = memo(({ index, style, data }) => {
    const {
        items,
        matchedRows = [],
        highlightedRowIndex = null,
        headers,
    } = data
    const isCurrentMatch = highlightedRowIndex === index
    const isHighlighted = matchedRows.includes(index)

    if (items[index] === 'buffer') {
        return (
            <div style={{ ...style, top: style.top + 5 }}>
                <p style={{ lineHeight: '40px', fontSize: 20 }}>Orphans</p>
            </div>
        )
    }

    // We are rendering an extra item for the placeholder
    if (data.renderedItems) {
        const placeholder = (
            <div
                id="action-row-placeholder"
                className={styles.actionRowPlaceholder}
            />
        )

        if (
            data.renderedItems.overscanStopIndex ===
            data.renderedItems.visibleStopIndex
        ) {
            if (!items[index]) {
                return placeholder
            }
        } else if (index >= data.renderedItems.overscanStopIndex) {
            return placeholder
        }
    }

    return (
        <Draggable
            isDragDisabled={!data.isEditing}
            draggableId={String(items[index])}
            index={index}
            key={items[index]}
        >
            {(provided) => (
                <Action
                    onCheckboxChange={data.onCheckboxChange}
                    provided={provided}
                    style={{
                        ...style,
                        top: style.top + 5,
                    }}
                    runbookId={data.runbookId}
                    id={items[index]}
                    rowIndex={index}
                    headers={headers}
                    isHighlighted={isHighlighted}
                    isCurrentMatch={isCurrentMatch}
                />
            )}
        </Draggable>
    )
}, areEqual)

class ActionsList extends React.Component {
    state = { renderedItems: null }

    isShiftDown = false
    previouslySelectedAction = null
    componentDidUpdate(prevProps) {
        const { focusedAction, clearFocusedAction } = this.props
        if (!prevProps.focusedAction && focusedAction) {
            let actionIndex = this.getItemData().indexOf(focusedAction)
            this.props.listRef.current.scrollToItem(actionIndex, 'center')
            clearFocusedAction()
        }
        if (prevProps.currentMatch !== this.props.currentMatch) {
            if (this.props.listRef && this.props.listRef.current) {
                this.props.listRef.current.resetAfterIndex(0, true)
            }
        }
    }

    clearCombineClass() {
        let faded = document.getElementsByClassName(COMBINE_CLASS_NAME)
        for (let i = 0; i < faded.length; i++) {
            faded[i].classList.remove(COMBINE_CLASS_NAME)
        }
    }

    updateIsShiftDown = (e) => {
        this.isShiftDown = !!e.shiftKey
    }

    getItemData() {
        const { actionUids, orphanedUids } = this.props
        let itemData = actionUids
        if (orphanedUids.length) {
            itemData = [...actionUids, 'buffer', ...orphanedUids]
        }

        return itemData
    }

    handleDragStart = (draggableItem) => {
        const actionRowPlaceholder = document.getElementById(
            'action-row-placeholder'
        )

        if (actionRowPlaceholder) {
            actionRowPlaceholder.style.display = 'block'
            actionRowPlaceholder.style.top = `${
                draggableItem.source.index * ROW_SIZE + 3
            }px`
        }
    }

    handleDragUpdate = (update) => {
        const { combine, destination } = update

        const actionRowPlaceholder = document.getElementById(
            'action-row-placeholder'
        )

        // Show the placeholder at the position where the action row will be dragged
        if (destination) {
            if (actionRowPlaceholder) {
                actionRowPlaceholder.style.display = 'block'
                actionRowPlaceholder.style.top = `${
                    destination.index * ROW_SIZE + 3
                }px`
            }
        } else {
            // Hide the placeholder if the action will be combined
            if (actionRowPlaceholder) {
                actionRowPlaceholder.style.display = 'none'
            }
        }

        // if combine is null, clear the fade class
        if (combine) {
            document
                .getElementById(`${combine.draggableId}-card`)
                .classList.add(COMBINE_CLASS_NAME)
        } else {
            this.clearCombineClass()
        }
    }

    handleDragEnd = (result) => {
        const { source, destination, combine } = result
        const { runbookId, moveAction } = this.props
        const itemData = this.getItemData()

        if (destination) {
            const newIndex = destination.index
            // if source.index is bigger than new, the action was moved up
            // else, it was moved down
            // if equal, it was dropped in the same place
            if (newIndex === source.index) return
            if (newIndex < source.index) {
                moveAction(
                    runbookId,
                    itemData[source.index],
                    itemData[newIndex - 1],
                    false
                )
            } else {
                moveAction(
                    runbookId,
                    itemData[source.index],
                    itemData[newIndex],
                    false
                )
            }
        } else if (combine) {
            moveAction(
                runbookId,
                itemData[source.index],
                +combine.draggableId,
                true
            )
            this.clearCombineClass()
        }

        // Hide placeholder
        const actionRowPlaceholder = document.getElementById(
            'action-row-placeholder'
        )

        if (actionRowPlaceholder) {
            actionRowPlaceholder.style.display = 'none'
        }
    }

    handleActionCheckboxChange = (action) => {
        const { actionUids, selectedActions } = this.props
        action = action.props.action

        if (
            this.isShiftDown &&
            selectedActions.length &&
            this.previouslySelectedAction
        ) {
            let startIndex = actionUids.indexOf(
                this.previouslySelectedAction.uid
            )
            let endIndex = actionUids.indexOf(action.uid)

            if (startIndex > endIndex) {
                ;[startIndex, endIndex] = [endIndex, startIndex]
            }

            for (let i = startIndex; i <= endIndex; i++) {
                this.props.actions.updateAction(
                    actionUids[i],
                    this.props.runbookId,
                    {
                        selected: !this.previouslySelectedAction.selected,
                    }
                )
            }
        } else {
            this.props.actions.updateAction(action.uid, this.props.runbookId, {
                selected: !action.selected,
            })
        }
        this.previouslySelectedAction = action
    }

    getRowSize = (index) => {
        const currentMatchIndex = this.props?.matchedRows[
            this.props?.currentMatch
        ]
        const isCurrentMatch = index === currentMatchIndex
        const extraOffset = isCurrentMatch ? 3 : 0
        return ROW_SIZE + extraOffset
    }

    renderActions() {
        const { renderedItems } = this.state
        const { runbookId, isEditing, actionUids, activeSession } = this.props
        if (actionUids.length === 0) return null

        const itemData = this.getItemData()

        const HEADERS = getHeaders({
            isEditing: Boolean(isEditing),
            activeSession: activeSession, // could be null || number
        })

        return (
            <AutoSizer>
                {({ height, width }) => (
                    <DragDropContext
                        onDragStart={this.handleDragStart}
                        onDragUpdate={this.handleDragUpdate}
                        onDragEnd={this.handleDragEnd}
                    >
                        <Droppable
                            droppableId="droppable"
                            mode="virtual"
                            renderClone={(provided, snapshot, rubric) => (
                                <Action
                                    onCheckboxChange={
                                        this.handleActionCheckboxChange
                                    }
                                    provided={provided}
                                    isDragging={snapshot.isDragging}
                                    runbookId={runbookId}
                                    rowIndex={rubric.source.index}
                                    id={itemData[rubric.source.index]}
                                    headers={HEADERS}
                                />
                            )}
                            isCombineEnabled
                        >
                            {(provided, snapshot) => {
                                const itemCount = snapshot.isUsingPlaceholder
                                    ? itemData.length + 1
                                    : itemData.length

                                return (
                                    <VariableSizeList
                                        className={styles.fixedSizeList}
                                        onItemsRendered={(renderedItems) =>
                                            this.setState({
                                                renderedItems,
                                            })
                                        }
                                        height={height}
                                        width={width}
                                        ref={this.props.listRef}
                                        itemCount={itemCount}
                                        itemSize={this.getRowSize}
                                        outerRef={provided.innerRef}
                                        itemData={{
                                            items: itemData,
                                            runbookId,
                                            isEditing,
                                            onCheckboxChange: this
                                                .handleActionCheckboxChange,
                                            renderedItems: renderedItems,
                                            matchedRows:
                                                this.props?.matchedRows ?? [],
                                            highlightedRowIndex:
                                                this.props?.matchedRows
                                                    ?.length > 0
                                                    ? this.props?.matchedRows[
                                                          this.props
                                                              ?.currentMatch
                                                      ]
                                                    : null,
                                            headers: HEADERS,
                                        }}
                                    >
                                        {Row}
                                    </VariableSizeList>
                                )
                            }}
                        </Droppable>
                    </DragDropContext>
                )}
            </AutoSizer>
        )
    }

    render() {
        const {
            runbookLoading,
            actionsLoading,
            sessionLoading,
            isEditing,
            activeSession,
        } = this.props
        const HEADERS = getHeaders({
            isEditing: Boolean(isEditing),
            activeSession: activeSession, // could be null || number
        })
        return (
            <>
                <div className={styles.listHeader}>
                    {HEADERS.filter(Boolean).map((header, index) => (
                        <div
                            key={index}
                            style={getColumnStyle(header)}
                            className={styles.headerCell}
                        >
                            {header.content}
                        </div>
                    ))}
                </div>
                <div onClick={this.updateIsShiftDown} className={styles.list}>
                    {runbookLoading || actionsLoading || sessionLoading ? (
                        <Spinner intent="primary" />
                    ) : (
                        this.renderActions() || (
                            <DataPlaceholder
                                title="No results have been found"
                                img={noActionsImage}
                            />
                        )
                    )}
                </div>
            </>
        )
    }
}

const mapStateToProps = (state) => {
    const runbook = getRunbook(state, state.ui.runbook.activeRunbook)
    const actionUids = getVisibleActionUids(
        state,
        state.ui.runbook.activeRunbook
    )
    const orphanedUids = getVisibleOrphans(
        state,
        state.ui.runbook.activeRunbook
    )

    return {
        selectedActions: getSelectedActions(
            state,
            state.ui.runbook.activeRunbook
        ),
        runbookId: runbook.id,
        isEditing: runbook.isEditing,
        activeSession: runbook.activeSession,
        actionUids,
        orphanedUids,
        runbookLoading:
            state.ui.runbook.runbookLoading ||
            (state.runbooks.templates?.loadingSelectedTemplateActions ?? false),
        actionsLoading: runbook.actions.loading,
        sessionLoading: runbook.sessionLoading,
        focusedAction: state.ui.runbook.focusedAction,
        actionsById: runbook.actions.byId,
        resultIndexes: runbook.actions.resultIndexes,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        dispatch,
        clearFocusedAction: bindActionCreators(clearFocusedAction, dispatch),
        moveAction: bindActionCreators(moveAction, dispatch),
        actions: bindActionCreators(
            {
                updateAction: (actionId, runbookId, data) => {
                    return updateAction(runbookId, actionId, data)
                },
                setDepthLevel,
            },
            dispatch
        ),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ActionsList)
